/* eslint-disable func-names */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-useless-escape */

import { config } from './config';
import { AnalyticsObject, SiteInfo } from './interfaces';
import { getBrowserName, getDeviceType, convertBigQueryDateToDate } from './utils';

/**
 * Return the url with analytics tags filtered out
 * @param {string} fullUrlString Full URL string
 * @returns {string} URL with analytics tags filtered out
 * @example getAnalyticsFilteredURL('https://www.example.com/page?utm_source=google') // returns 'https://example.com/page'
 */
function getAnalyticsFilteredURL(fullUrlString: string): string {
  const fullUrl = new URL(fullUrlString);
  let retUrl = fullUrl.origin;
  if (retUrl.includes('www.')) {
    retUrl = `${retUrl.split('www.')[0]}${retUrl.split('www.')[1]}`;
  }
  retUrl = `${retUrl}${fullUrl.pathname}${fullUrl.hash}`;
  if (retUrl.endsWith('/')) {
    retUrl = retUrl.slice(0, -1);
  }
  return retUrl;
}

/**
 * Gets if analytics debug mode is enabled
 * @returns {boolean} True if analytics debug mode is enabled
 */
function analyticsIsDebugMode(): boolean {
  return localStorage.getItem('analyticsDebugMode') === 'true';
}

/**
 * Gets if analytics verbose debug mode is enabled
 * @returns {boolean} True if analytics verbose debug mode is enabled
 */
function analyticsIsDebugModeVerbose(): boolean {
  return localStorage.getItem('analyticsDebugModeVerbose') === 'true';
}

/**
 * Logs to console if debug mode is enabled
 * All parameters are passed to console.log()
 */
export function analyticsDebugLog(...args: any[]): void {
  try {
    if (analyticsIsDebugMode() || analyticsIsDebugModeVerbose()) {
      // eslint-disable-next-line prefer-rest-params
      console.log.apply(null, args);
    }
  } catch (error) {
    // Silently fail so as not to clutter console if on a site
  }
}

let logEventFailedCount: number = 0;

/**
 * Performs the actual submitting of the vent to the server
 * @param newEvent Event data
 */
async function submitLog(newEvent: AnalyticsObject): Promise<void> {
  try {
    analyticsDebugLog('Submitting event: ', newEvent);
    const maxAttemptsAllowedToFail = 3;
    if (logEventFailedCount >= maxAttemptsAllowedToFail) {
      analyticsDebugLog('Logging canceled due to failure threshold hit. Event body: ', newEvent);
      return; // Don't attempt logging anymore if failed more than the threshold
    }
    let hotelParam = new URL(window.location.href)?.searchParams?.get("hotel") ?? '';
    if (navigator.sendBeacon) { // Use sendbeacon if the browser supports it
      try {

        navigator.sendBeacon(`${config.HOTEL_PARAM_WORKAROUND.includes(hotelParam) ? config.HIO_API_ENDPOINT_WWW : config.HIO_API_ENDPOINT}/toolsAPI/tools/add`, JSON.stringify(newEvent));
        return;
      } catch (e) {
        console.log(e);
      }
    }
    await fetch(`${config.HOTEL_PARAM_WORKAROUND.includes(hotelParam) ? config.HIO_API_ENDPOINT_WWW : config.HIO_API_ENDPOINT}/toolsAPI/tools/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      keepalive: true,
      body: JSON.stringify(newEvent),
    });
  } catch (error) {
    logEventFailedCount++;
  }
}

const LoggedEvents: string[] = [];

function getSessionStorageLoggedEvents(): string[] {
  const storageValue = sessionStorage.getItem('LoggedEvents');
  if (storageValue) {
    return JSON.parse(storageValue);
  }
  return [];
}

/**
 * Checks if an event has been logged before.
 * Checks both in memory and session storage.
 * @param category {string} category of the event
 * @returns {boolean} true if the event has been logged before
 */
function getIfLoggedEvent(category: string): boolean {
  const memoryValue = LoggedEvents;
  const storageValue = getSessionStorageLoggedEvents();
  if (memoryValue.includes(category) || storageValue.includes(category)) {
    return true;
  }
  return false;
}

/**
 * Marks an event as logged
 * @param category {string} category of the event
 */
function markLoggedEvent(category: string): void {
  if (!LoggedEvents.includes(category)) {
    LoggedEvents.push(category);
  }
  sessionStorage.setItem('LoggedEvents', JSON.stringify(LoggedEvents));
}

export function clearItemFromLoggedEvents(category: string): void {
  const sessionValue = getSessionStorageLoggedEvents();
  const mergedValueNoDuplicates = Array.from(new Set([...LoggedEvents, ...sessionValue]));
  if (mergedValueNoDuplicates.includes(category)) {
    mergedValueNoDuplicates.splice(mergedValueNoDuplicates.indexOf(category), 1);
    LoggedEvents.splice(LoggedEvents.indexOf(category), 1); // Update in-memory LoggedEvents
  }
  sessionStorage.setItem('LoggedEvents', JSON.stringify(mergedValueNoDuplicates));
}

function logToCustomerGoogleAnalytics(args: any): void {
  // Gtag already handles views
  const categoryMap = {
    engagement_conversion_sale: {
      googleCategory: 'hovr_booking_engagement_conversion_sale',
      data: () => {
        const dataObject: any = {};
        if (args.potentialTotalValue) {
          dataObject.potentialTotalValue = args.potentialTotalValue;
        }
        if (args.interactedDate) {
          dataObject.interactedDate = convertBigQueryDateToDate(args.interactedDate);
        }
        return dataObject;
      },
    },
    direct_cta_conversion_sale: {
      googleCategory: 'hovr_booking_direct_cta_conversion_sale',
      data: () => {
        const dataObject: any = {};
        if (args.potentialTotalValue) {
          dataObject.potentialTotalValue = args.potentialTotalValue;
        }
        if (args.interactedDate) {
          dataObject.interactedDate = convertBigQueryDateToDate(args.interactedDate);
        }
        return dataObject;
      },
    },
    no_interaction_checkout: {
      googleCategory: 'hovr_booking_no_interaction_checkout',
      data: () => {
        const dataObject: any = {};
        if (args.potentialTotalValue) {
          dataObject.potentialTotalValue = args.potentialTotalValue;
        }
        return dataObject;
      },
    },
  };
  if (categoryMap[args.category] !== null && categoryMap[args.category] !== undefined) {
    logToGoogleAnalytics(
      categoryMap[args.category].googleCategory,
      categoryMap[args.category].data(),
    );
  }
}

/**
 * Log an event to the server
 * @param args {AnalyticsObject} Properties/columns to add
 * @param siteInfo {SiteInfo} Site information
 * @param allowMultipleLogs {boolean} If true, will allow logging the same event multiple times
 */
export async function logAnalytic(
  args: AnalyticsObject,
  siteInfo: SiteInfo,
  allowMultipleLogs = false,
): Promise<void> {
  try {
    if (!args.category) {
      console.log('Event log is missing category, unable to log.');
      return;
    }
    const singleLogCategories = ['pageload', 'confirmation_pageload'];
    const forceSingleLog = singleLogCategories.includes(args.category);
    const canLogMultiple = allowMultipleLogs && !forceSingleLog;

    if (getIfLoggedEvent(args.category) && !canLogMultiple) {
      analyticsDebugLog('Event already logged, skipping: ', args.category);
      return;
    // eslint-disable-next-line no-else-return
    } else {
      // Only push if the event is not already in the array
      // As a minor memory performance optimization
      if (!getIfLoggedEvent(args.category)) {
        markLoggedEvent(args.category);
      }
    }

    const newEvent = { ...args };
    const pageFullUrl = window.location.href;

    newEvent.timestamp = (new Date().toISOString());
    newEvent.referrer = document.referrer;
    newEvent.device = getDeviceType();
    newEvent.siteID = siteInfo.id === 'ignore' ? args.backupSiteId : siteInfo.id; // In the case of the site ID being 'ignore', we use the backup site ID which is pulled from the URL param or the script tag data attribute
    newEvent.domain = window.location.hostname;
    newEvent.product = 'bookingengine';
    newEvent.pageURL = getAnalyticsFilteredURL(pageFullUrl);
    // Booking engines often have sensitive data in the url, so we filter out even the full url
    newEvent.fullURL = getAnalyticsFilteredURL(pageFullUrl);
    newEvent.pageTitle = document.title;
    newEvent.browser = getBrowserName();
    newEvent.organizationID = siteInfo.organizationId;
    logToCustomerGoogleAnalytics(args);
    await submitLog(newEvent);
  } catch (error) {
    console.log('Error logging analytic: ', error);
  }
}

/**
 * Log an event to Google Analytics
 * @param category {string}
 * @param data {object} Optional data to log
 */
export function logToGoogleAnalytics(category: string, data: any = {}): void {
  try {
    // @ts-ignore
    if (window.gtag) { // gtag is preferred
      // @ts-ignore
      window.gtag('event', category, data);
      // @ts-ignore
    } else if (window.dataLayer) { // Fallback to dataLayer
      // @ts-ignore
      window.dataLayer.push({ event: category, ...data });
    }
  } catch (error) {
    analyticsDebugLog('Google Tag Error: ', error);
  }
}
